import {FC, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useMutation, useQueryClient} from 'react-query'
import clsx from 'clsx'
import {OnboardingItem} from '../core/_models'
import {markOnboardingItem, setOnboardingDismissed} from '../core/_requests'
import {ONBOARDING_QUERY_KEY, useOnboardingProgress} from '../hooks/useOnboardingProgress'
import {getLockedText, ONBOARDING_ITEMS} from '../onboardingItems'
import {openNewTask} from '../openNewTask'
import {WorkingDayModal} from './WorkingDayModal'

// Checklist de "Primeiros passos" no topo do dashboard. O progresso vem do servidor (dados reais),
// então volta a aparecer marcado sozinho quando a pessoa faz o passo em qualquer tela.
export const OnboardingChecklist: FC = () => {
  const {data} = useOnboardingProgress()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showWorkday, setShowWorkday] = useState(false)

  const refresh = () => queryClient.invalidateQueries(ONBOARDING_QUERY_KEY)
  const dismiss = useMutation(() => setOnboardingDismissed(true), {onSuccess: refresh})
  const mark = useMutation((key: string) => markOnboardingItem(key), {onSuccess: refresh})

  if (!data || data.dismissed) return null

  // O destaque vai para o próximo passo que a pessoa consegue fazer agora (não para um bloqueado).
  const nextKey = data.items.find((item) => !item.done && !item.locked_reason)?.key

  const handleAction = (item: OnboardingItem) => {
    const meta = ONBOARDING_ITEMS[item.key]

    if (meta.action === 'workday') {
      setShowWorkday(true)
      return
    }
    if (meta.action === 'new-task') {
      openNewTask()
      return
    }
    if (item.manual && !item.done) mark.mutate(item.key)
    if (meta.href) window.open(meta.href, '_blank', 'noopener,noreferrer')
    else if (meta.to) navigate(meta.to)
  }

  return (
    <>
      <div className='dash-section onboarding-checklist' data-testid='onboarding-checklist'>
        <div className='dash-section-head'>
          <div>
            <h2 className='dash-section-title mb-1'>
              {data.completed ? 'Tudo pronto!' : 'Primeiros passos'}
            </h2>
            <div className='dash-section-subtitle'>
              {data.completed
                ? 'Você concluiu os primeiros passos. Pode ocultar este bloco.'
                : `${data.done_count} de ${data.total} concluídos. Cada passo leva direto à tela certa.`}
            </div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isLoading}
          >
            Ocultar
          </button>
        </div>

        <progress
          className='onboarding-progress'
          value={data.done_count}
          max={data.total}
          aria-label={`${data.done_count} de ${data.total} passos concluídos`}
        />

        <ul className='onboarding-list'>
          {data.items.map((item) => {
            const meta = ONBOARDING_ITEMS[item.key]
            const locked = !item.done && item.locked_reason !== null

            return (
              <li
                key={item.key}
                className={clsx('onboarding-item', {'is-done': item.done, 'is-locked': locked})}
              >
                <i
                  className={clsx('onboarding-item-icon bi', {
                    'bi-check-circle-fill': item.done,
                    'bi-lock': locked,
                    'bi-circle': !item.done && !locked,
                  })}
                  aria-hidden='true'
                ></i>
                <div className='onboarding-item-text'>
                  <div className='onboarding-item-title'>{meta.title}</div>
                  <div className='onboarding-item-benefit'>
                    {locked ? getLockedText(item.locked_reason!, data.role) : meta.benefit}
                  </div>
                </div>
                {!item.done && !locked && (
                  <button
                    type='button'
                    className={clsx('btn btn-sm', {
                      'btn-primary': item.key === nextKey,
                      'btn-light': item.key !== nextKey,
                    })}
                    onClick={() => handleAction(item)}
                  >
                    {meta.cta}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </div>

      <WorkingDayModal show={showWorkday} onHide={() => setShowWorkday(false)} />
    </>
  )
}
